<template>
  <v-app class="app-background">
    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" md="10" lg="8">
          <v-card elevation="10" class="rounded-xl overflow-hidden">
            <!-- Header -->
            <v-card-title class="primary white--text py-6 px-8">
              <v-icon large color="white" class="mr-4">{{ isEditMode ? 'mdi-calendar-edit' : 'mdi-calendar-clock' }}</v-icon>
              <div>
                <span class="text-h5 font-weight-bold d-block">{{ isEditMode ? 'Editar Agenda de Médico' : 'Configuración de Agenda' }}</span>
                <span class="subtitle-2 white--text opacity-80">
                  {{ isEditMode ? 'Modifique los horarios de atención existentes del profesional' : 'Defina los horarios de atención para los profesionales' }}
                </span>
              </div>
            </v-card-title>

            <v-form ref="form" v-model="valid" @submit.prevent="validateCreateForm">
              <v-card-text class="pa-8">
                
                <!-- Alerta de Modo Edición -->
                <v-alert v-if="isEditMode" type="info" text border="left" class="mb-6 rounded-lg">
                  <div class="d-flex align-center justify-space-between flex-wrap">
                    <div>
                      <v-icon left color="info">mdi-calendar-edit</v-icon>
                      <strong>Se cargó la agenda existente para este profesional.</strong>
                      <div class="text-caption">Modifique los horarios específicos por día y presione "Actualizar Agenda" para guardar los cambios.</div>
                    </div>
                    <v-btn small text color="info" class="mt-2 mt-sm-0" @click="resetAgendaForm">
                      <v-icon left small>mdi-plus-circle-outline</v-icon> Crear nueva desde cero
                    </v-btn>
                  </div>
                </v-alert>

                <!-- Sección Profesional -->
                <v-row>
                  <v-col cols="12">
                    <span class="text-subtitle-1 font-weight-bold primary--text mb-2 d-block">
                      <v-icon left color="primary">mdi-doctor</v-icon> Profesional
                    </span>
                    <v-select
                      v-model="selected"
                      :items="nombres"
                      item-text="text"
                      item-value="value"
                      label="Seleccione un médico"
                      outlined
                      dense
                      class="rounded-lg"
                      prepend-inner-icon="mdi-account-tie"
                      :loading="loadingAgenda"
                      :rules="[v => !!v || 'El profesional es requerido']"
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title>No hay médicos disponibles</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>

                <v-divider class="my-6"></v-divider>

                <!-- Sección Fechas -->
                <span class="text-subtitle-1 font-weight-bold primary--text mb-4 d-block">
                  <v-icon left color="primary">mdi-calendar-range</v-icon> Período de Validez
                </span>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="menuDesdeDia"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="account_info.desdeDia"
                          label="Fecha Desde"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          v-bind="attrs"
                          v-on="on"
                          class="rounded-lg"
                          :rules="[v => !!v || 'Fecha desde es requerida']"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="account_info.desdeDia"
                        @input="menuDesdeDia = false"
                        color="primary"
                        locale="es"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                  
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="menuHastaDia"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="account_info.hastaDia"
                          label="Fecha Hasta"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          outlined
                          dense
                          v-bind="attrs"
                          v-on="on"
                          class="rounded-lg"
                          :rules="[v => !!v || 'Fecha hasta es requerida']"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="account_info.hastaDia"
                        @input="menuHastaDia = false"
                        color="primary"
                        locale="es"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>

                <v-divider class="my-6"></v-divider>

                <!-- Sección Días de la semana -->
                <span class="text-subtitle-1 font-weight-bold primary--text mb-4 d-block">
                  <v-icon left color="primary">mdi-calendar-week</v-icon> Días de la Semana
                </span>
                <v-row justify="center" class="mb-4">
                  <v-col cols="12">
                     <v-btn-toggle
                        v-model="selectedDays"
                        multiple
                        class="d-flex flex-wrap justify-center rounded-lg"
                        color="primary"
                        borderless
                      >
                        <v-btn v-for="day in daysOptions" :key="day.value" :value="day.value" class="ma-1 rounded-lg px-4" outlined>
                          {{ day.text }}
                        </v-btn>
                      </v-btn-toggle>
                      <div v-if="selectedDays.length === 0" class="text-center error--text text-caption mt-2">
                        Debe seleccionar al menos un día
                      </div>
                  </v-col>
                </v-row>

                <!-- Sección Horarios Diferenciados por Día -->
                <template v-if="selectedDays.length > 0">
                  <v-divider class="my-6"></v-divider>
                  <div class="d-flex align-center justify-space-between mb-4">
                    <span class="text-subtitle-1 font-weight-bold primary--text">
                      <v-icon left color="primary">mdi-clock-outline</v-icon> Horario de Atención por Día
                    </span>
                    <v-btn small text color="primary" @click="copiarPrimerHorario" v-if="selectedDays.length > 1">
                      <v-icon left small>mdi-content-copy</v-icon> Copiar horario del primer día a todos
                    </v-btn>
                  </div>

                  <v-row>
                    <v-col cols="12" v-for="dayVal in selectedDays" :key="dayVal">
                      <v-card outlined class="pa-4 rounded-lg grey lighten-5">
                        <div class="d-flex align-center mb-3">
                          <v-chip color="primary" class="font-weight-bold mr-3" label small>
                            {{ getDayText(dayVal) }}
                          </v-chip>
                          <span class="caption text--secondary">Defina la hora de inicio, fin y duración para los {{ getDayText(dayVal) }}</span>
                        </div>
                        <v-row dense>
                          <v-col cols="12" sm="4">
                            <v-menu
                              :ref="'menuDesde_' + dayVal"
                              v-model="diasHorarios[dayVal].menuDesde"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              max-width="290px"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  v-model="diasHorarios[dayVal].desdeHora"
                                  label="Hora Inicio"
                                  prepend-inner-icon="mdi-clock-start"
                                  readonly
                                  outlined
                                  dense
                                  v-bind="attrs"
                                  v-on="on"
                                  class="rounded-lg"
                                  :rules="[v => !!v || 'Hora inicio requerida']"
                                ></v-text-field>
                              </template>
                              <v-time-picker
                                v-if="diasHorarios[dayVal].menuDesde"
                                v-model="diasHorarios[dayVal].desdeHora"
                                full-width
                                format="24hr"
                                @click:minute="$refs['menuDesde_' + dayVal][0].save(diasHorarios[dayVal].desdeHora)"
                                color="primary"
                              ></v-time-picker>
                            </v-menu>
                          </v-col>

                          <v-col cols="12" sm="4">
                            <v-menu
                              :ref="'menuHasta_' + dayVal"
                              v-model="diasHorarios[dayVal].menuHasta"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              max-width="290px"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  v-model="diasHorarios[dayVal].hastaHora"
                                  label="Hora Fin"
                                  prepend-inner-icon="mdi-clock-end"
                                  readonly
                                  outlined
                                  dense
                                  v-bind="attrs"
                                  v-on="on"
                                  class="rounded-lg"
                                  :rules="[v => !!v || 'Hora fin requerida']"
                                ></v-text-field>
                              </template>
                              <v-time-picker
                                v-if="diasHorarios[dayVal].menuHasta"
                                v-model="diasHorarios[dayVal].hastaHora"
                                full-width
                                format="24hr"
                                @click:minute="$refs['menuHasta_' + dayVal][0].save(diasHorarios[dayVal].hastaHora)"
                                color="primary"
                              ></v-time-picker>
                            </v-menu>
                          </v-col>

                          <v-col cols="12" sm="4">
                            <v-text-field
                              v-model="diasHorarios[dayVal].duracion"
                              label="Duración (min)"
                              prepend-inner-icon="mdi-timer-sand"
                              type="number"
                              step="5"
                              min="5"
                              outlined
                              dense
                              class="rounded-lg"
                              :rules="[v => !!v || 'Duración requerida', v => v > 0 || 'Debe ser mayor a 0']"
                            ></v-text-field>
                          </v-col>
                        </v-row>
                      </v-card>
                    </v-col>
                  </v-row>
                </template>

              </v-card-text>

              <!-- Acciones -->
              <v-card-actions class="primary lighten-5 pa-6">
                <v-spacer></v-spacer>
                <v-btn
                  color="grey darken-1"
                  text
                  class="rounded-lg mr-2"
                  @click="$router.back()"
                >
                  Cancelar
                </v-btn>
                <v-btn
                  color="primary"
                  class="rounded-lg px-8"
                  elevation="2"
                  type="submit"
                  :loading="loading || loadingAgenda"
                  :disabled="!valid || selectedDays.length === 0"
                >
                  <v-icon left>{{ isEditMode ? 'mdi-calendar-edit' : 'mdi-content-save' }}</v-icon>
                  {{ isEditMode ? 'Actualizar Agenda' : 'Guardar Agenda' }}
                </v-btn>
              </v-card-actions>
            </v-form>

            <!-- Loading Overlay -->
            <v-overlay :value="dialog" absolute>
              <v-progress-circular indeterminate size="64"></v-progress-circular>
            </v-overlay>

          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { getProfesionales, createWorkCalendar, deleteWorkCalendar, decodeProfesional } from '@/services/monolito';
import { getTodosTurnos } from '@/services/appointmentsApi';
import { validationMixin } from 'vuelidate';

export default {
  name: "workcalendar",
  mixins: [validationMixin],
  data: function() {
    return {
      valid: false,
      loading: false,
      loadingAgenda: false,
      isEditMode: false,
      menuDesdeDia: false,
      menuHastaDia: false,
      
      selected: null,
      nombres: [],
      profesional: [],
      
      originalAgenda: {
        desdeDia: null,
        hastaDia: null
      },

      account_info: {
        desdeDia: new Date().toISOString().substr(0, 10),
        hastaDia: new Date().toISOString().substr(0, 10),
        desdeHora: '12:00',
        hastaHora: '20:00',
        duracion: '30',
        diaSemanas: [],
        diasHorarios: {}
      },
      
      diasHorarios: {
        Lunes: { desdeHora: '12:00', hastaHora: '20:00', duracion: '30', menuDesde: false, menuHasta: false },
        Martes: { desdeHora: '12:00', hastaHora: '20:00', duracion: '30', menuDesde: false, menuHasta: false },
        Miercoles: { desdeHora: '12:00', hastaHora: '20:00', duracion: '30', menuDesde: false, menuHasta: false },
        Jueves: { desdeHora: '12:00', hastaHora: '20:00', duracion: '30', menuDesde: false, menuHasta: false },
        Viernes: { desdeHora: '12:00', hastaHora: '20:00', duracion: '30', menuDesde: false, menuHasta: false },
        Sabado: { desdeHora: '12:00', hastaHora: '20:00', duracion: '30', menuDesde: false, menuHasta: false },
      },

      selectedDays: [], // Array de valores seleccionados ['Lunes', 'Martes', etc.]
      daysOptions: [
        { text: 'Lunes', value: 'Lunes' },
        { text: 'Martes', value: 'Martes' },
        { text: 'Miércoles', value: 'Miercoles' },
        { text: 'Jueves', value: 'Jueves' },
        { text: 'Viernes', value: 'Viernes' },
        { text: 'Sábado', value: 'Sabado' },
      ],
      
      dialog: false,
    }
  },
  watch: {
    selected: function(newVal) {
      if (newVal) {
        this.loadDoctorAgenda(newVal);
      } else {
        this.resetAgendaForm();
      }
    }
  },
  created(){
    this.getProfesional()
  },
  methods: {
    getDayText(val) {
      const match = this.daysOptions.find(d => d.value === val);
      return match ? match.text : val;
    },

    copiarPrimerHorario() {
      if (this.selectedDays.length <= 1) return;
      const firstDay = this.selectedDays[0];
      const source = this.diasHorarios[firstDay];
      this.selectedDays.forEach(day => {
        if (day !== firstDay && this.diasHorarios[day]) {
          this.diasHorarios[day].desdeHora = source.desdeHora;
          this.diasHorarios[day].hastaHora = source.hastaHora;
          this.diasHorarios[day].duracion = source.duracion;
        }
      });
    },

    async getProfesional() {
      try {
        this.profesional = await getProfesionales();
        this.nombres = decodeProfesional(this.profesional).map(p => ({
            text: p.text, 
            value: p.value 
        }));
      } catch (e) {
        console.error("Error cargando profesionales", e);
      }
    },

    async loadDoctorAgenda(profesionalId) {
      this.loadingAgenda = true;
      try {
        const response = await getTodosTurnos(profesionalId);
        const citas = response.citas || [];

        if (citas.length > 0) {
          let minDate = null;
          let maxDate = null;
          
          const dayMap = {
            1: 'Lunes',
            2: 'Martes',
            3: 'Miercoles',
            4: 'Jueves',
            5: 'Viernes',
            6: 'Sabado',
            0: 'Domingo'
          };

          const perDayData = {};

          citas.forEach(cita => {
            const startDateStr = cita.start ? cita.start.split(' ')[0] : null;
            const startTimeStr = cita.start ? cita.start.split(' ')[1]?.substring(0, 5) : null;
            const endTimeStr = cita.end ? cita.end.split(' ')[1]?.substring(0, 5) : null;

            if (startDateStr) {
              if (!minDate || startDateStr < minDate) minDate = startDateStr;
              if (!maxDate || startDateStr > maxDate) maxDate = startDateStr;

              const d = new Date(startDateStr + 'T00:00:00');
              const dayName = dayMap[d.getDay()];
              if (dayName) {
                if (!perDayData[dayName]) {
                  perDayData[dayName] = {
                    minTime: '23:59',
                    maxTime: '00:00',
                    durations: []
                  };
                }
                if (startTimeStr && startTimeStr < perDayData[dayName].minTime) perDayData[dayName].minTime = startTimeStr;
                if (endTimeStr && endTimeStr > perDayData[dayName].maxTime) perDayData[dayName].maxTime = endTimeStr;

                if (cita.start && cita.end) {
                  const startDt = new Date(cita.start.replace(' ', 'T'));
                  const endDt = new Date(cita.end.replace(' ', 'T'));
                  const diffMinutes = Math.round((endDt - startDt) / 60000);
                  if (diffMinutes > 0) perDayData[dayName].durations.push(diffMinutes);
                }
              }
            }
          });

          if (minDate && maxDate) {
            this.account_info.desdeDia = minDate;
            this.account_info.hastaDia = maxDate;

            const validDayValues = this.daysOptions.map(d => d.value);
            const activeDays = Object.keys(perDayData).filter(day => validDayValues.includes(day));
            this.selectedDays = activeDays;

            activeDays.forEach(day => {
              const dData = perDayData[day];
              const minDur = dData.durations.length ? Math.min(...dData.durations) : 30;
              if (this.diasHorarios[day]) {
                this.diasHorarios[day].desdeHora = dData.minTime !== '23:59' ? dData.minTime : '12:00';
                this.diasHorarios[day].hastaHora = dData.maxTime !== '00:00' ? dData.maxTime : '20:00';
                this.diasHorarios[day].duracion = String(minDur);
              }
            });

            this.originalAgenda = {
              desdeDia: minDate,
              hastaDia: maxDate
            };
            this.isEditMode = true;
          } else {
            this.isEditMode = false;
          }
        } else {
          this.isEditMode = false;
        }
      } catch (e) {
        console.error("Error al cargar la agenda del profesional:", e);
        this.isEditMode = false;
      } finally {
        this.loadingAgenda = false;
      }
    },

    resetAgendaForm() {
      this.isEditMode = false;
      this.originalAgenda = { desdeDia: null, hastaDia: null };
      this.account_info = {
        desdeDia: new Date().toISOString().substr(0, 10),
        hastaDia: new Date().toISOString().substr(0, 10),
        desdeHora: '12:00',
        hastaHora: '20:00',
        duracion: '30',
        diaSemanas: [],
        diasHorarios: {}
      };
      this.selectedDays = [];
    },
    
    async validateCreateForm() {
      if (!this.$refs.form.validate()) return;
      if (this.selectedDays.length === 0) return;

      this.create_work_calendar();
    },

    async create_work_calendar(){
      this.loading = true;
      this.dialog = true;

      let idProfesional;
      
      if (this.selected != null){
         idProfesional = this.selected;
      } else {
        idProfesional = 0;
      }
      
      this.account_info.diaSemanas = this.selectedDays;

      // Armar payload de diasHorarios limpios
      const cleanDiasHorarios = {};
      this.selectedDays.forEach(day => {
        if (this.diasHorarios[day]) {
          cleanDiasHorarios[day] = {
            desdeHora: this.diasHorarios[day].desdeHora,
            hastaHora: this.diasHorarios[day].hastaHora,
            duracion: this.diasHorarios[day].duracion
          };
        }
      });
      this.account_info.diasHorarios = cleanDiasHorarios;

      if (this.selectedDays.length > 0 && this.diasHorarios[this.selectedDays[0]]) {
        const first = this.diasHorarios[this.selectedDays[0]];
        this.account_info.desdeHora = first.desdeHora;
        this.account_info.hastaHora = first.hastaHora;
        this.account_info.duracion = first.duracion;
      }

      try {
        if (this.isEditMode && this.originalAgenda.desdeDia && this.originalAgenda.hastaDia) {
          // Limpiar turnos libres del rango previamente cargado
          await deleteWorkCalendar(idProfesional, {
            desdeDia: this.originalAgenda.desdeDia,
            hastaDia: this.originalAgenda.hastaDia
          });
        }

        await createWorkCalendar(idProfesional, this.account_info);
        const titleMsg = this.isEditMode ? 'Agenda actualizada' : 'Agenda creada';
        const detailMsg = this.isEditMode ? 'Se actualizó la agenda con éxito' : 'Se creó la agenda con éxito';

        await this.$alert({ title: titleMsg, message: detailMsg, color: 'success' });
        
        if (this.selected) {
          await this.loadDoctorAgenda(this.selected);
        }
      } catch (error) {
        console.error('❌ Error en create_work_calendar:', error);
        
        if (error.isBackendIssue) {
          await this.$alert({ 
            title: 'Problema Confirmado del Servidor', 
            message: `DIAGNÓSTICO COMPLETO: ${error.message}\n\nRECOMENDACIÓN: ${error.recommendation || 'Contacte al administrador del sistema.'}`, 
            color: 'warning' 
          });
        } else if (error.response && error.response.status === 401) {
          await this.$alert({ 
            title: 'Error de Configuración', 
            message: 'Problema de permisos en el servidor (401). Contacte a soporte.', 
            color: 'error' 
          });
        } else {
          const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
          await this.$alert({ title: 'Error', message: errorMessage, color: 'error' });
        }
      } finally {
        this.loading = false;
        this.dialog = false;
      }
    },
  }
}
</script>

<style scoped>
.app-background {
  background: #f5f7fa;
}
.opacity-80 {
  opacity: 0.8;
}
</style>